import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import Row from '../../shared/components/Row';
import { I18nUtils } from '../../../i18n/I18nUtils';
import Card from '../../shared/components/Card';
import CardSimpleHeader from '../../shared/components/CardSimpleHeader';
import CardBody from '../../shared/components/CardBody';
import { bindActionCreators } from 'redux';
import { ReduxFormUtils } from '../../shared/utils/ReduxFormUtils';
import { FormText } from '../../shared/components/FormText';
import * as contractorsActions from '../actions/contractorsActions';
import { FormSubmit } from '../../shared/components/FormSubmit';
import { FormSwitch } from '../../shared/components/FormSwitch';
import { FormType } from '../../shared/model/FormType';
import { FormArea } from '../../shared/components/FormArea';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Well from '../../shared/components/Well';
import debounce from 'lodash/debounce';
import { Nif } from '../../shared/model/objectvalue/Nif';
import ConfirmModal from '../../shared/components/confirm-modal/ConfirmModal';

const SCRIPT_EXPLANATION = `
    INSTRUCCIONES:

    Al script le llega un array en la variable "documents". Cada elemento del array tiene, actualmente,
    tres propiedades definidas:
        - id: number
        - required: boolean
        - document_state_id: number (1-7)
            * Valores posibles:
                1. VALIDATED
                2. PENDING_VALIDATION
                3. PENDING
                4. EXPIRED
                5. REJECTED
                6. ARCHIVED
                7. DOES_NOT_APPLY

    El resultado del script es númerico (number) entre 1-3:
        1. ALL_CORRECT
        2. SOME_COMPULSORY_DOCUMENT_NOT_VALIDATED
        3. SOME_OPTIONAL_DOCUMENT_NOT_VALIDATED

    Cada uno de estos estados tiene un color; el nombre que indicado arriba es lo de menos. Lo importante
    es que ante una casuística se devuelva un color u otro. El script debe estar hecho en Javascript y su
    última instrucción devoler un valor (no hacer un return).

    EJEMPLO:

    El script de ejemplo hace lo siguiente:
        - Si encuentra un documento con id 1 devuelve el estado 3.
        - En caso contrario, devuelve 1.

            var result = 1;
            for ( let i = 0; i < documents.length; i++ ) {
                let document = documents[i];
                if ( document.id == 1 ) {
                    result = 3;
                    break;
                }
            }
            result;

`;

const FORM = {
    NAME: 'ContractorsForm',
    FIELDS: {
        API: 'api',
        API_DOMAIN: 'apiDomain',
        API_BASE_URL: 'apiBaseUrl',
        API_USERNAME: 'apiUsername',
        API_PASSWORD: 'apiPassword',
        ADMIN: 'admin', // CREATE
        ADMIN_NAME: 'adminName',
        ADMIN_SURNAMES: 'adminSurnames',
        ADMIN_EMAIL: 'adminEmail',
        ADMIN_PASSWORD: 'adminPassword',
        ADMINS: 'admins', // UPDATE, READ
        CONTRACTOR_NAME: 'contractorName',
        CONTRACTOR_NIF: 'contractorNif',
        CONTRACTOR_PHONE: 'contractorPhone',
        CONTRACTOR_EMAIL: 'contractorEmail',
        CONTRACTOR_ADDRESS: 'contractorAddress',
        CONTRACTOR_CCC: 'contractorCcc',
        CONTRACTOR_CONTACT_PERSON: 'contractorContactPerson',
        CONTRACTOR_SHOW_CREATE_TASK_WINDOW: 'contractorShowCreateTaskWindow',
        CONTRACTOR_SCRIPT_WORKER_STATES: 'contractorScriptWorkerStates',
        CONTRACTOR_SKIP_RNT_CHECK: 'contractorSkipRntCheck',
        CONTRACTOR_SKIP_AUTOMATIC_HACIENDA_CHECK: 'contractorSkipAutomaticHaciendaCheck',
        SUBMIT: 'submit',
    },
    SYNC_ERRORS: 'syncErrors',
};

class ContractorsForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            displayScriptExplanation: false,
            isNifFieldInitialized: false,
            showLoader: false,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            contractorSearch,
            change,
        } = this.props;

        if ( contractorSearch ) {
            if ( prevProps.contractorSearch !== contractorSearch ) {
                change(FORM.FIELDS.CONTRACTOR_NAME, contractorSearch.name());
                change(FORM.FIELDS.CONTRACTOR_PHONE, contractorSearch.phone());
                change(FORM.FIELDS.CONTRACTOR_EMAIL, contractorSearch.email());
                change(FORM.FIELDS.CONTRACTOR_ADDRESS, contractorSearch.address());
                change(FORM.FIELDS.CONTRACTOR_CCC, contractorSearch.ccc());
                change(FORM.FIELDS.CONTRACTOR_CONTACT_PERSON, contractorSearch.contactPerson());
            }
        }
    }

    componentWillUnmount() {
        this.props.contractorsActions.clearContractorsForm();
    }

    onShowFormLoader = (showLoader) => {
        this.setState({ showLoader });
        if ( showLoader ) {
            setTimeout(() => this.onShowFormLoader(false), 1000);
        }
    };

    onInitializeNifField = (isNifFieldInitialized) => {
        this.setState({ isNifFieldInitialized });
    };

    onChangeNifField = (nif) => {
        const { isNifFieldInvalid } = this.props;

        if ( nif && !isNifFieldInvalid ) {
            this.onInitializeNifField(true);
            this.onShowFormLoader(true);
            this.searchContractor(nif);
        } else {
            this.props.contractorsActions.clearContractorsForm();
            this.clearFields();
        }
    };

    onChangeNifFieldDebounced = debounce(this.onChangeNifField, 1000);

    clearFields = () => {
        const { change } = this.props;
        change(FORM.FIELDS.API_DOMAIN, '');
        change(FORM.FIELDS.API_BASE_URL, '');
        change(FORM.FIELDS.API_USERNAME, '');
        change(FORM.FIELDS.API_PASSWORD, '');
        change(FORM.FIELDS.ADMIN_NAME, '');
        change(FORM.FIELDS.ADMIN_SURNAMES, '');
        change(FORM.FIELDS.ADMIN_EMAIL, '');
        change(FORM.FIELDS.ADMIN_PASSWORD, '');
        change(FORM.FIELDS.ADMINS, '');
        change(FORM.FIELDS.CONTRACTOR_NAME, '');
        change(FORM.FIELDS.CONTRACTOR_PHONE, '');
        change(FORM.FIELDS.CONTRACTOR_EMAIL, '');
        change(FORM.FIELDS.CONTRACTOR_ADDRESS, '');
        change(FORM.FIELDS.CONTRACTOR_CCC, '');
        change(FORM.FIELDS.CONTRACTOR_CONTACT_PERSON, '');
        change(FORM.FIELDS.CONTRACTOR_SHOW_CREATE_TASK_WINDOW, false);
        change(FORM.FIELDS.CONTRACTOR_SCRIPT_WORKER_STATES, '');
        change(FORM.FIELDS.CONTRACTOR_SCRIPT_WORKER_STATES, '');
    };

    searchContractor = (nif) => {
        const { isNifFieldInvalid } = this.props;
        if ( !isNifFieldInvalid ) {
            this.props.contractorsActions.searchContractor(nif);
        }
    };

    handleCloserContractorCreateFromCompanyConfirmModal = () => {
        this.props.contractorsActions.setContractorCreateFromCompanyConfirmModalDisplay(false);
    };

    render() {
        const {
            displayScriptExplanation,
        } = this.state;

        const {
            loading,
            formValues,
            handleSubmit,
            showFields,
            type,
        } = this.props;

        const {
            title,
            onSubmit,
            submitLabel,
            disabledForm,
        } = this.handleTypeForm();

        const isShowFields = ( type === FormType.UPDATE ) || showFields;
        const onChangeValueSideEffect = type === FormType.CREATE ? this.onChangeNifFieldDebounced : undefined;

        return (
            <React.Fragment>
                <Row>
                    <Card loading={loading}>
                        <CardSimpleHeader title={title}/>
                        <CardBody>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Row>
                                    <Field
                                        col="col-md-3"
                                        testId="form-nif"
                                        name={FORM.FIELDS.CONTRACTOR_NIF}
                                        label={I18nUtils.tr('TR_NIF')}
                                        placeholder={I18nUtils.tr('TR_NUMERO_DE_IDENTIFICACION_FISCAL')}
                                        disabled={disabledForm}
                                        component={FormText}
                                        onChangeValueSideEffect={onChangeValueSideEffect}
                                    />
                                    {
                                        isShowFields ? (
                                            <Field
                                                col="col-md-6"
                                                testId="form-name"
                                                name={FORM.FIELDS.CONTRACTOR_NAME}
                                                label={I18nUtils.tr('TR_RAZON_SOCIAL')}
                                                placeholder={I18nUtils.tr('TR_RAZON_SOCIAL')}
                                                disabled={disabledForm}
                                                component={FormText}
                                            />
                                        ) : null
                                    }
                                </Row>
                                {
                                    isShowFields ? (
                                        <React.Fragment>
                                            <Row>
                                                <Field
                                                    col="col-md-3"
                                                    testId="form-contact-person"
                                                    name={FORM.FIELDS.CONTRACTOR_CONTACT_PERSON}
                                                    label={I18nUtils.tr('TR_PERSONA_DE_CONTACTO')}
                                                    placeholder={I18nUtils.tr('TR_PERSONA_DE_CONTACTO')}
                                                    disabled={disabledForm}
                                                    component={FormText}
                                                />
                                                <Field
                                                    col="col-md-3"
                                                    testId="form-phone"
                                                    label={I18nUtils.tr('TR_TELEFONO')}
                                                    placeholder={I18nUtils.tr('TR_NUMERO_DE_TELEFONO')}
                                                    name={FORM.FIELDS.CONTRACTOR_PHONE}
                                                    disabled={disabledForm}
                                                    component={FormText}
                                                />
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-email"
                                                    label={I18nUtils.tr('TR_EMAIL_DE_CONTRATA')}
                                                    placeholder={I18nUtils.tr('TR_EMAIL_DE_CONTRATA')}
                                                    name={FORM.FIELDS.CONTRACTOR_EMAIL}
                                                    disabled={disabledForm}
                                                    component={FormText}
                                                />
                                            </Row>
                                            <Row>
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-address"
                                                    label={I18nUtils.tr('TR_DIRECCION')}
                                                    placeholder={I18nUtils.tr('TR_DIRECCION')}
                                                    name={FORM.FIELDS.CONTRACTOR_ADDRESS}
                                                    disabled={disabledForm}
                                                    component={FormText}
                                                />
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-ccc"
                                                    label={I18nUtils.tr('TR_CODIGO_CUENTA_COTIZACION_CCC')}
                                                    placeholder={I18nUtils.tr('TR_CODIGO_CUENTA_COTIZACION_CCC')}
                                                    name={FORM.FIELDS.CONTRACTOR_CCC}
                                                    disabled={disabledForm}
                                                    component={FormText}
                                                />
                                            </Row>
                                            <Row>
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-allow-create-task"
                                                    name={FORM.FIELDS.CONTRACTOR_SHOW_CREATE_TASK_WINDOW}
                                                    label={I18nUtils.tr('TR_PERMISO_PARA_CREAR_TAREAS')}
                                                    labelDisabled={I18nUtils.tr('TR_NO')}
                                                    labelEnabled={I18nUtils.tr('TR_SI')}
                                                    disabled={disabledForm}
                                                    component={FormSwitch}
                                                />
                                                {/*<Field*/}
                                                {/*    col="col-md-6"*/}
                                                {/*    testId="form-enabled"*/}
                                                {/*    name={FORM.FIELDS.CONTRACTOR_ENABLED}*/}
                                                {/*    label={I18nUtils.tr('TR_ESTADO')}*/}
                                                {/*    labelDisabled={I18nUtils.tr('TR_INACTIVO')}*/}
                                                {/*    labelEnabled={I18nUtils.tr('TR_ACTIVO')}*/}
                                                {/*    disabled={disabledForm}*/}
                                                {/*    component={FormSwitch}*/}
                                                {/*/>*/}
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-allow-create-task"
                                                    name={FORM.FIELDS.CONTRACTOR_SKIP_RNT_CHECK}
                                                    label={I18nUtils.tr('TR_SALTAR_VALIDACION_RNT')}
                                                    labelDisabled={I18nUtils.tr('TR_NO')}
                                                    labelEnabled={I18nUtils.tr('TR_SI')}
                                                    disabled={disabledForm}
                                                    component={FormSwitch}
                                                />
                                            </Row>
                                            <Row>
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-allow-create-task"
                                                    name={FORM.FIELDS.CONTRACTOR_SKIP_AUTOMATIC_HACIENDA_CHECK}
                                                    label={I18nUtils.tr('TR_SALTAR_VALIDACION_AUTOMATICA_CERTIFICADO_HACIENDA')}
                                                    labelDisabled={I18nUtils.tr('TR_NO')}
                                                    labelEnabled={I18nUtils.tr('TR_SI')}
                                                    disabled={disabledForm}
                                                    component={FormSwitch}
                                                />
                                            </Row>
                                            {
                                                formValues.admin ? (
                                                    <Well style={{ marginTop: 10, marginBottom: 20 }}>
                                                        <Row>
                                                            <div className="col-sm-12">
                                                                <div className="lead"
                                                                     style={{ marginBottom: 20, fontSize: 20 }}>
                                                                    {I18nUtils.tr('TR_USUARIO_ADMINISTRADOR')}
                                                                </div>
                                                            </div>
                                                        </Row>
                                                        <Row>
                                                            <Field
                                                                col="col-md-6"
                                                                testId="form-admin-name"
                                                                label={I18nUtils.tr('TR_NOMBRE')}
                                                                placeholder={I18nUtils.tr('TR_NOMBRE')}
                                                                name={FORM.FIELDS.ADMIN_NAME}
                                                                component={FormText}
                                                            />
                                                            <Field
                                                                col="col-md-6"
                                                                testId="form-admin-surnames"
                                                                label={I18nUtils.tr('TR_APELLIDO_S')}
                                                                placeholder={I18nUtils.tr('TR_APELLIDO_S')}
                                                                name={FORM.FIELDS.ADMIN_SURNAMES}
                                                                component={FormText}
                                                            />
                                                        </Row>
                                                        <Row>
                                                            <Field
                                                                col="col-md-6"
                                                                testId="form-admin-email"
                                                                label={I18nUtils.tr('TR_EMAIL')}
                                                                placeholder={I18nUtils.tr('TR_EMAIL')}
                                                                name={FORM.FIELDS.ADMIN_EMAIL}
                                                                component={FormText}
                                                            />
                                                            <Field
                                                                col="col-md-6"
                                                                testId="form-admin-password"
                                                                label={I18nUtils.tr('TR_CONTRASENA')}
                                                                placeholder={I18nUtils.tr('TR_CONTRASENA')}
                                                                name={FORM.FIELDS.ADMIN_PASSWORD}
                                                                component={FormText}
                                                                password={true}
                                                            />
                                                        </Row>
                                                    </Well>
                                                ) : null
                                            }
                                            {
                                                !formValues.admin && formValues.admins ? (
                                                    <Well style={{ marginTop: 10, marginBottom: 20 }}>
                                                        <Row>
                                                            <div className="col-sm-12">
                                                                <div className="lead"
                                                                     style={{ marginBottom: 10, fontSize: 20 }}>
                                                                    {I18nUtils.tr('TR_ADMINISTRADORES')}
                                                                </div>
                                                            </div>
                                                        </Row>
                                                        <Row>
                                                            <div className="col-sm-12">
                                                                {
                                                                    formValues.admins.map((admin) =>
                                                                        <div key={admin.id}>{admin.email}</div>,
                                                                    )
                                                                }
                                                            </div>
                                                        </Row>
                                                    </Well>
                                                ) : null
                                            }
                                            <Row>
                                                <Field
                                                    col="col-md-6"
                                                    testId="form-api-enabled"
                                                    name={FORM.FIELDS.API}
                                                    label={I18nUtils.tr('TR_API')}
                                                    labelEnabled={I18nUtils.tr('TR_ACTIVA')}
                                                    labelDisabled={I18nUtils.tr('TR_INACTIVA')}
                                                    disabled={disabledForm}
                                                    component={FormSwitch}
                                                />
                                            </Row>
                                            {
                                                formValues.api ? (
                                                    <Well>
                                                        <Row>
                                                            <Field
                                                                col="col-md-12"
                                                                name={FORM.FIELDS.API_DOMAIN}
                                                                label={I18nUtils.tr('TR_DOMINIO')}
                                                                placeholder={I18nUtils.tr('TR_DOMINIO_DE_LA_API')}
                                                                disabled={disabledForm}
                                                                component={FormText}
                                                            />
                                                        </Row>
                                                        <Row>
                                                            <Field
                                                                col="col-md-12"
                                                                name={FORM.FIELDS.API_BASE_URL}
                                                                label={I18nUtils.tr('TR_URL_BASE')}
                                                                placeholder={I18nUtils.tr('TR_RUTA_BASE_PARA_ACCEDER_A_LA_API')}
                                                                disabled={disabledForm}
                                                                component={FormText}
                                                            />
                                                        </Row>
                                                        <Row>
                                                            <Field
                                                                col="col-md-6"
                                                                name={FORM.FIELDS.API_USERNAME}
                                                                label={I18nUtils.tr('TR_USUARIO')}
                                                                placeholder={I18nUtils.tr('TR_USUARIO_PARA_ACCEDER_A_LA_API')}
                                                                disabled={disabledForm}
                                                                component={FormText}
                                                            />
                                                            <Field
                                                                col="col-md-6"
                                                                name={FORM.FIELDS.API_PASSWORD}
                                                                label={I18nUtils.tr('TR_CONTRASENA')}
                                                                placeholder={I18nUtils.tr('TR_CONTRASENA_DEL_USUARIO')}
                                                                disabled={disabledForm}
                                                                password={true}
                                                                component={FormText}
                                                            />
                                                        </Row>
                                                    </Well>
                                                ) : null
                                            }
                                            <Row>
                                                <Field
                                                    col="col-md-12"
                                                    rows={8}
                                                    name={FORM.FIELDS.CONTRACTOR_SCRIPT_WORKER_STATES}
                                                    label={I18nUtils.tr('TR_SCRIPT_ESTADO_TRABAJADOR')}
                                                    placeholder={I18nUtils.tr('TR_PRESIONE_SOBRE_EL_ICONO_PARA_MOSTRAR_LAS_INSTRUCCIONES')}
                                                    disabled={disabledForm}
                                                    resize={'vertical'}
                                                    component={FormArea}
                                                    icon={(
                                                        <OverlayTrigger
                                                            placement={'right'}
                                                            overlay={
                                                                <Tooltip
                                                                    children={I18nUtils.tr('TR_PRESIONAME_PARA_MOSTRAR_LAS_INSTRUCCIONES')}/>
                                                            }
                                                        >
                                                            <i
                                                                className="material-icons"
                                                                onClick={this.handleDisplayScriptExplanation}
                                                                children={'info'}
                                                            />
                                                        </OverlayTrigger>
                                                    )}
                                                />
                                                {
                                                    displayScriptExplanation ? (
                                                        <div className="col-md-12">
                                                            <pre>
                                                                <code>
                                                                    {SCRIPT_EXPLANATION}
                                                                </code>
                                                            </pre>
                                                        </div>
                                                    ) : null
                                                }
                                            </Row>
                                            {
                                                !disabledForm ? (
                                                    <Row>
                                                        <Field
                                                            label={submitLabel}
                                                            name={FORM.FIELDS.SUBMIT}
                                                            col={'col-md-6 col-md-offset-6'}
                                                            component={FormSubmit}
                                                        />
                                                    </Row>
                                                ) : null
                                            }
                                        </React.Fragment>
                                    ) : null
                                }
                            </form>
                        </CardBody>
                    </Card>
                </Row>
                {this.renderContractorCreateFromCompanyConfirmModal()}
            </React.Fragment>
        );
    }

    renderContractorCreateFromCompanyConfirmModal() {
        const {
            displayDialog,
            contractorsActions,
            contractorSave,
        } = this.props;

        return <ConfirmModal
            show={displayDialog}
            onHide={this.handleCloserContractorCreateFromCompanyConfirmModal}
            onAccept={() => contractorsActions.createContractor(contractorSave, null, true)}
            title={I18nUtils.tr('TR_PROMOCIONAR_SUBCONTRAT_A_CONTRATA')}
            bodyTitle={I18nUtils.tr('TR_LA_EMPRESA_QUE_INTENTA_ANYADIR_YA_EXISTE_COMO_SUBCONTRATA')}
            bodyText={I18nUtils.tr('TR_DESEA_PROMOCIONAR_ESTA_SUBCONTRATA_A_CONTRATA')}
        />;
    }

    handleDisplayScriptExplanation = () => {
        this.setState(prevState => ( { displayScriptExplanation: !prevState.displayScriptExplanation } ));
    };

    handleTypeForm = () => {
        switch ( this.props.type ) {
            case FormType.CREATE:
                return {
                    title: I18nUtils.tr('TR_NUEVO_CONTRATISTA'),
                    submitLabel: I18nUtils.tr('TR_CREAR'),
                    disabledForm: false,
                    onSubmit: this.handleSubmitCreate,
                };
            case FormType.READ:
                return {
                    title: I18nUtils.tr('TR_FICHA_CONTRATISTA'),
                    submitLabel: '',
                    disabledForm: true,
                    onSubmit: () => {},
                };
            case FormType.UPDATE:
                return {
                    title: I18nUtils.tr('TR_EDITAR_CONTRATISTA'),
                    submitLabel: I18nUtils.tr('TR_GUARDAR'),
                    disabledForm: false,
                    onSubmit: this.handleSubmitUpdate,
                };
            default:
        }
    };

    handleSubmitCreate = (values) => {
        const {
            contractorsActions,
            contractorSearch,
        } = this.props;

        contractorsActions.createContractor(values, contractorSearch);
    };

    handleSubmitUpdate = (values) => {
        const { contractor, contractorsActions } = this.props;
        contractorsActions.updateContractor(values, contractor);
    };

}

const validate = (values) => {
    const {
        admin,
        adminName,
        adminSurnames,
        adminEmail,
        adminPassword,
        contractorName,
        contractorNif,
    } = values;

    const errors = {};
    const emptyError = I18nUtils.tr('TR_CAMPO_OBLIGATORIO');

    if ( !contractorName ) errors.contractorName = emptyError;
    try {
        new Nif(contractorNif);
    } catch (e) {
        errors.contractorNif = e.message;
    }
    if ( admin ) {
        if ( !adminName ) errors.adminName = emptyError;
        if ( !adminSurnames ) errors.adminSurnames = emptyError;
        if ( !adminEmail ) errors.adminEmail = emptyError;
        if ( !adminPassword ) errors.adminPassword = emptyError;
    }

    return errors;
};

const mapStateToProps = (state, ownProps) => {
    const {
        contractor,
        type,
        loading,
    } = ownProps;

    const initialValues = {
        api: !!contractor && !!contractor.api_base_url,
        apiBaseUrl: contractor ? contractor.api_base_url : undefined,
        apiPassword: contractor ? contractor.api_password : undefined,
        apiUsername: contractor ? contractor.api_username : undefined,
        apiDomain: contractor ? contractor.domain : undefined,
        admin: type === FormType.CREATE,
        adminName: undefined,
        adminSurnames: undefined,
        adminEmail: undefined,
        adminPassword: undefined,
        admins: contractor && contractor.admins ? contractor.admins : [],
        contractorScriptWorkerStates: contractor ? contractor.script_worker_states : undefined,
        contractorShowCreateTaskWindow: contractor ? contractor.show_create_task_window : false,
        contractorName: contractor ? contractor.name : undefined, // Idéntico a contractor.company.name
        contractorNif: contractor ? contractor.nif : undefined, // Idéntico a contractor.company.nif
        contractorAddress: contractor && contractor.company ? contractor.company.address : undefined,
        contractorCcc: contractor && contractor.company ? contractor.company.ccc : undefined,
        contractorContactPerson: contractor && contractor.company ? contractor.company.contact_person : undefined,
        contractorEmail: contractor && contractor.company ? contractor.company.email : undefined,
        contractorPhone: contractor && contractor.company ? contractor.company.phone : undefined,
        contractorEnabled: contractor ? !contractor.disabled : false,
        contractorSkipAutomaticHaciendaCheck: contractor ? contractor.skip_certificado_hacienda_contratista_check : true,
        contractorSkipRntCheck: contractor ? contractor.skip_rnt_check : false,
    };

    const isNifFieldInvalid = !!(
        state.form[FORM.NAME] &&
        state.form[FORM.NAME][FORM.SYNC_ERRORS] &&
        state.form[FORM.NAME][FORM.SYNC_ERRORS][FORM.FIELDS.CONTRACTOR_NIF]
    );

    const stateContractors = state.contractors;

    const isLoading = loading || stateContractors.createContractorLoading || stateContractors.contractorsSearchLoading;
    return {
        initialValues,
        formValues: ReduxFormUtils.selectFormValues(state, FORM.NAME),
        isNifFieldInvalid: isNifFieldInvalid,
        contractorSearch: stateContractors.contractorSearch,
        displayDialog: stateContractors.displayDialog,
        contractorSave: stateContractors.contractorSave,
        loading: isLoading,
        showFields: stateContractors.showFields,
    };
};

const mapDispatchToProps = dispatch => ( {
    contractorsActions: bindActionCreators(contractorsActions, dispatch),
} );

export default connect(mapStateToProps, mapDispatchToProps)(
    reduxForm(
        {
            validate,
            form: FORM.NAME,
            enableReinitialize: true,
        },
    )(ContractorsForm),
);

ContractorsForm.propTypes = {
    type: PropTypes.string.isRequired,
    contractor: PropTypes.object,
    loading: PropTypes.bool,
};

ContractorsForm.defaultProps = {};



// WEBPACK FOOTER //
// ./src/app/contractors/components/ContractorsForm.jsx